import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Button, TextField, CircularProgress, Grid } from "@mui/material";
import dayjs from "dayjs";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";

const UsageHistory = () => {
  const [data, setData] = useState<any>(null);
  const [msisdn, setmsisdn] = useState<any>("");
  const agGridRef = useRef<AgGridReact | null>(null);
  const [action, setAction] = useState<Boolean>(false);
  const today = dayjs();
  const todayDate = today.format("YYYY-MM-DD");
  const oneMonthAgoDate = today.subtract(1, "month").format("YYYY-MM-DD");
  const [endDate, setEndDate] = useState<any>(todayDate);
  const [startDate, setStartDate] = useState<any>(oneMonthAgoDate);

  const exportGridDataAsCSV = () => {
    if (agGridRef.current) {
      agGridRef.current.api.exportDataAsCsv();
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: "start_time",
      headerName: "Date",
      filter: true,
      resizable: true,
      sortable: true,
    },
    {
      field: "ToNumber",
      headerName: "ToNumber",
      filter: true,
      resizable: true,
      sortable: true,
    },
    {
      field: "Type",
      headerName: "Type",
      filter: true,
      resizable: true,
      sortable: true,
    },

    {
      field: "Roaming",
      headerName: "Roaming",
      filter: true,
      resizable: true,
      sortable: true,
    },
    {
      field: "Usage",
      headerName: "Usage",
      filter: true,
      resizable: true,
      sortable: true,
    },

    {
      field: "Charge",
      headerName: "Charge",
      filter: true,
      resizable: true,
      sortable: true,
    },
  ];

  const handleGetUsageHistory = async () => {
    setData(null);
    toast.dismiss();
    if (!msisdn.trim() || !startDate || !endDate || msisdn.trim().length <= 9) {
      toast.error("Please enter valid inputs.");
      return;
    }
    setAction(true);
    requestHistory();
  };
  const onGridReady = (params: { api: { sizeColumnsToFit: () => void } }) => {
    params.api.sizeColumnsToFit();
  };
  const requestHistory = async () => {
    await axiosInterceptorInstance
      .get(
        `/dashboard/UsageHistory?Msisdn=${msisdn.trim()}&StartDate=${startDate}&EndDate=${endDate}`
      )
      .then((res: any) => {
        setAction(false);
        const apiresponse = JSON.parse(res.data.body);
        if (apiresponse.History.length == 0) {
          toast.error("Record not found.");
        } else {
          setData(apiresponse.History);
        }
      })
      .catch((error: any) => {
        setAction(false);
        toast.error(error.message);
      });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">Usage History</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                className="formControl"
                label="MSISDN"
                variant="outlined"
                value={msisdn}
                onChange={(e) => setmsisdn(e.target.value)}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={startDate ? dayjs(startDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const formatted = newValue.format("YYYY-MM-DD");
                      setStartDate(formatted);
                      if (endDate && formatted > endDate) {
                        setEndDate(formatted);
                      }
                    } else {
                      setStartDate("");
                    }
                  }}
                  maxDate={endDate ? dayjs(endDate) : today}
                  disableFuture
                  slotProps={{
                    actionBar: {
                      actions: ["clear"],
                    },
                    textField: {
                      fullWidth: true,
                    },
                    field: {
                      readOnly: true,
                      onKeyDown: (e: React.KeyboardEvent) => e.preventDefault(),
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={endDate ? dayjs(endDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const formatted = newValue.format("YYYY-MM-DD");
                      setEndDate(formatted);
                    } else {
                      setEndDate("");
                    }
                  }}
                  minDate={startDate ? dayjs(startDate) : undefined}
                  maxDate={today}
                  disableFuture
                  slotProps={{
                    actionBar: {
                      actions: ["clear"],
                    },
                    textField: {
                      fullWidth: true,
                    },
                    field: {
                      readOnly: true,
                      onKeyDown: (e: React.KeyboardEvent) => e.preventDefault(),
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid
              item
              xs={4}
              style={{
                margin: "0 auto",
              }}
            >
              {!action && (
                <Button
                  onClick={handleGetUsageHistory}
                  className="dButton dButton2"
                  variant="contained"
                >
                  Search
                </Button>
              )}
              {action && (
                <Button className="dButton dButton2" variant="contained">
                  <CircularProgress color="inherit" size={30} />
                </Button>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "5px",
        }}
      >
        <button
          className="small-btn"
          style={{ visibility: data ? "visible" : "hidden" }}
          onClick={() => {
            if (data) {
              exportGridDataAsCSV();
            }
          }}
        >
          Export as CSV
        </button>
      </div>
      {data && (
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={agGridRef}
            paginationPageSize={8}
            pagination={true}
            domLayout="autoHeight"
            rowData={data}
            enableCellTextSelection={true}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
          ></AgGridReact>
        </div>
      )}
    </motion.div>
  );
};

export default UsageHistory;
